import { supabase } from './supabase.js';
export * from './pi-parse.js';

// Match lines: remembered supplier_product_code → our SKU → barcode.
export async function matchLines(supplierId, lines) {
  const codes = [...new Set(lines.map(l => l.code))];
  const [{ data: remembered }, { data: bySku }, { data: byBarcode }] = await Promise.all([
    supabase.from('supplier_products')
      .select('supplier_product_code, pack_size, product:products(id, name, sku, image_url)')
      .eq('supplier_id', supplierId).in('supplier_product_code', codes),
    supabase.from('products').select('id, name, sku, image_url').in('sku', codes),
    supabase.from('products').select('id, name, sku, image_url, barcode').in('barcode', codes),
  ]);
  const remMap = new Map((remembered ?? []).map(r => [r.supplier_product_code.toLowerCase(), r.product]));
  // จำนวนชิ้นต่อกล่องที่จำไว้จากการนำเข้าครั้งก่อน (เช่น Nalgene = 6) — เติมช่อง "ต่อกล่อง" ให้เอง
  const packMap = new Map((remembered ?? [])
    .filter(r => Number(r.pack_size) > 1)
    .map(r => [r.supplier_product_code.toLowerCase(), Number(r.pack_size)]));
  const skuMap = new Map((bySku ?? []).map(p => [p.sku?.toLowerCase(), p]));
  const barMap = new Map((byBarcode ?? []).map(p => [p.barcode?.toLowerCase(), p]));

  // รหัสบนใบ = SKU เก่าที่ PD เปลี่ยนไปแล้ว → ตามไปหารหัสปัจจุบัน (public.fn_sku_current อ่านประวัติ rename ของ PD)
  // เคส 1 ต.ค. 69: ใบ Snow Peak Festival รหัส 100261ET0200067 แต่ PD เปลี่ยนเป็น ET-26SU00100067 → หาไม่เจอ
  const renMap = new Map();
  const missing = codes.filter(c => { const k = String(c ?? '').toLowerCase(); return k && !remMap.has(k) && !skuMap.has(k) && !barMap.has(k); });
  if (missing.length) {
    try {
      const { data: ren } = await supabase.rpc('fn_sku_current', { p_codes: missing });
      if (ren?.length) {
        const { data: renProducts } = await supabase.from('products')
          .select('id, name, sku, image_url').in('sku', [...new Set(ren.map(r => r.sku))]);
        const bySkuNew = new Map((renProducts ?? []).map(p => [p.sku?.toLowerCase(), p]));
        for (const r of ren) {
          const p = bySkuNew.get(String(r.sku).toLowerCase());
          if (p) renMap.set(String(r.code).toLowerCase(), p);
        }
      }
    } catch (e) { console.warn('ตามรหัสที่เปลี่ยนใน PD ไม่สำเร็จ (ข้ามไป):', e?.message ?? e); }
  }

  return lines.map(l => {
    const k = l.code.toLowerCase();
    const product = remMap.get(k) ?? skuMap.get(k) ?? barMap.get(k) ?? renMap.get(k) ?? null;
    const source = remMap.has(k) ? 'remembered' : skuMap.has(k) ? 'sku' : barMap.has(k) ? 'barcode' : renMap.has(k) ? 'renamed' : null;
    const pack = packMap.get(k);
    // pack ที่จำไว้เติมเฉพาะตอนที่ยังไม่มีค่า (ร่างที่บันทึกไว้ถือค่าของตัวเอง ห้ามทับ)
    return { ...l, product, source, ...(pack && !(Number(l.pack) > 1) ? { pack, _packFrom: 'remembered' } : {}) };
  });
}

// ร่างที่บันทึกไว้เก็บ "สำเนา" สินค้าที่จับคู่ ณ ตอนสร้างร่าง — PD แก้ทีหลัง (เปลี่ยนรหัส/ชื่อ/ลบตัวซ้ำ) ร่างไม่รู้
// ฟังก์ชันนี้เทียบทุกบรรทัดกับแคตตาล็อกปัจจุบัน: อัปเดตชื่อ/รหัส/รูป · สินค้าที่ถูกลบ = ปลดออก · แล้วลองจับคู่ใหม่
// ให้ทุกบรรทัดที่ยังไม่มีคู่ (จำไว้ / SKU / บาร์โค้ด / รหัสที่ PD เปลี่ยน) — ไม่แตะบรรทัดที่จับคู่ได้อยู่แล้ว
export async function refreshLines(supplierId, lines) {
  const ids = [...new Set(lines.filter(l => l.lineType === 'product' && l.product?.id).map(l => l.product.id))];
  const { data: cur } = ids.length
    ? await supabase.from('products').select('id, name, sku, image_url, is_active').in('id', ids)
    : { data: [] };
  const byId = new Map((cur ?? []).map(p => [p.id, p]));
  let updated = 0, dropped = 0, rematched = 0;
  const out = lines.map(l => {
    if (l.lineType !== 'product' || !l.product?.id) return l;
    const p = byId.get(l.product.id);
    if (!p) { dropped++; return { ...l, product: null, source: null, _dropped: l.product.sku || l.product.name || 'สินค้าเดิม' }; }
    const next = { id: p.id, name: p.name, sku: p.sku, image_url: p.image_url ?? null };
    const changed = next.sku !== l.product.sku || next.name !== l.product.name || next.image_url !== (l.product.image_url ?? null);
    const flag = p.is_active === false ? { _inactive: true } : { _inactive: undefined };
    if (changed) { updated++; return { ...l, ...flag, product: next, _renamedFrom: next.sku !== l.product.sku ? l.product.sku : l._renamedFrom }; }
    return { ...l, ...flag };
  });
  const need = out.map((l, i) => [l, i]).filter(([l]) => l.lineType === 'product' && !l.product && l.code);
  if (need.length && supplierId) {
    const m = await matchLines(supplierId, need.map(([l]) => l));
    m.forEach((ml, k) => {
      if (ml.product) { rematched++; out[need[k][1]] = { ...ml, _dropped: undefined }; }
    });
  }
  return { lines: out, stats: { updated, dropped, rematched, stillMissing: need.length - rematched } };
}

// Suggest the next SKU for a brand (prefix from brand letters, running number). Editable.
export async function nextSku(brandName) {
  const prefix = (String(brandName ?? '').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase()) || 'XX';
  const { data } = await supabase.from('products')
    .select('sku').ilike('sku', `${prefix}-%`)
    .order('sku', { ascending: false }).limit(1);
  const last = data?.[0]?.sku?.match(/(\d+)$/)?.[1];
  const n = last ? String(Number(last) + 1).padStart(Math.max(last.length, 4), '0') : '0001';
  return `${prefix}-${n}`;
}
